import { useMemo } from 'react';
import { parseEntityRef } from '@backstage/catalog-model';
import type { ComponentType } from 'react';
import type { Visit } from '@backstage/plugin-home';
import {
  BellIcon,
  ClockIcon,
  DocumentCleanIcon,
  GlobeIcon,
  GridIcon,
  LinkIcon,
  UsersIcon,
} from '../../components/icons';
import type { PaletteGroup, PaletteItem, PaletteSearchResult } from './types';

type PaletteIcon = ComponentType<{ className?: string }>;

/*
 * A palette is a shortlist, not a result page: rows past `MAX_ROWS` are a
 * scroll the keyboard has to walk, and the full result set is one Enter away
 * on /search. The per-group cap divides that budget evenly across however
 * many groups actually have results, rather than a fixed number each — 4
 * groups get 4 rows apiece, 3 groups get 5, one group gets the whole budget.
 */
export const MAX_ROWS = 16;

type GroupSpec = { label: string; order: number };

/** Catalog results split by kind — the mock's eyebrows are kinds, not collators. */
const KIND_GROUPS: Record<string, GroupSpec> = {
  component: { label: 'Components', order: 1 },
  api: { label: 'APIs', order: 2 },
  repository: { label: 'Repositories', order: 3 },
  system: { label: 'Systems', order: 4 },
  domain: { label: 'Domains', order: 5 },
  resource: { label: 'Resources', order: 6 },
  group: { label: 'Teams', order: 7 },
  user: { label: 'People', order: 8 },
};

const TYPE_GROUPS: Record<string, GroupSpec> = {
  techdocs: { label: 'Documentation', order: 20 },
  announcements: { label: 'Announcements', order: 21 },
};

const KIND_ICONS: Record<string, PaletteIcon> = {
  api: LinkIcon,
  user: UsersIcon,
  group: UsersIcon,
  domain: GlobeIcon,
  system: GlobeIcon,
};

const TYPE_ICONS: Record<string, PaletteIcon> = {
  techdocs: DocumentCleanIcon,
  announcements: BellIcon,
};

/*
 * Where the palette goes with an empty query. Every entry is a route the portal
 * actually serves — the scaffolder is not installed, so the mock's "Scaffold a
 * new service" is the register flow, which is the equivalent this portal has.
 */
const JUMP_TO: PaletteItem[] = [
  {
    id: 'jump:catalog',
    title: 'Catalog',
    meta: 'Page',
    to: '/catalog',
    icon: GridIcon,
    isIdentifier: false,
  },
  {
    id: 'jump:docs',
    title: 'Documentation',
    meta: 'Page',
    to: '/documentation',
    icon: DocumentCleanIcon,
    isIdentifier: false,
  },
  {
    id: 'jump:announcements',
    title: 'Announcements',
    meta: 'Page',
    to: '/announcements',
    icon: BellIcon,
    isIdentifier: false,
  },
];

/** Kinds and owner-name words that are acronyms, not words to capitalize. */
const ACRONYMS: Record<string, string> = { api: 'API', adr: 'ADR' };

function titleCase(value: string): string {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(
      word =>
        ACRONYMS[word.toLowerCase()] ??
        word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
}

/** `group:default/data-platform` reads as `Data Platform` in the meta column. */
function ownerLabel(owner: unknown): string | undefined {
  if (typeof owner !== 'string' || !owner) {
    return undefined;
  }
  try {
    return titleCase(parseEntityRef(owner).name);
  } catch {
    return titleCase(owner);
  }
}

function groupSpecFor(result: PaletteSearchResult): GroupSpec {
  const kind = (result.document as { kind?: string }).kind?.toLowerCase();
  if (result.type === 'techdocs' || result.type === 'announcements') {
    return TYPE_GROUPS[result.type];
  }
  if (kind && KIND_GROUPS[kind]) {
    return KIND_GROUPS[kind];
  }
  return { label: titleCase(result.type), order: 30 };
}

function iconFor(result: PaletteSearchResult): PaletteIcon {
  const typeIcon = TYPE_ICONS[result.type];
  if (typeIcon) {
    return typeIcon;
  }
  const kind = (result.document as { kind?: string }).kind?.toLowerCase();
  return (kind && KIND_ICONS[kind]) || GridIcon;
}

function metaFor(result: PaletteSearchResult, groupLabel: string): string {
  const document = result.document as { kind?: string; owner?: string };
  // The eyebrow already names the kind for catalog rows; the meta leads with
  // the singular of it so a row still reads standalone when scanned.
  const lead = document.kind ? titleCase(document.kind) : groupLabel;
  const owner = ownerLabel(document.owner);
  return owner ? `${lead} · ${owner}` : lead;
}

function toPaletteItem(
  result: PaletteSearchResult,
  groupLabel: string,
): PaletteItem {
  return {
    id: `${result.type}:${result.document.location}`,
    title: result.document.title,
    meta: metaFor(result, groupLabel),
    to: result.document.location,
    icon: iconFor(result),
    // A catalog title is the entity name; a TechDocs or announcement title is
    // the page's prose heading.
    isIdentifier: !TYPE_GROUPS[result.type],
  };
}

/** Recents are already ordered newest-first by the visits API. */
function visitToPaletteItem(visit: Visit): PaletteItem {
  const isDoc = visit.pathname.startsWith('/documentation');
  let meta = 'Page';
  if (isDoc) {
    meta = 'Documentation';
  } else if (visit.entityRef) {
    meta = titleCase(parseEntityRef(visit.entityRef).kind);
  }
  return {
    id: `recent:${visit.id}`,
    title: visit.name,
    meta,
    to: visit.pathname,
    icon: ClockIcon,
    isIdentifier: !isDoc && !!visit.entityRef,
  };
}

function capped(groups: PaletteGroup[]): PaletteGroup[] {
  if (groups.length === 0) {
    return [];
  }
  const perGroup = Math.max(1, Math.floor(MAX_ROWS / groups.length));
  const out: PaletteGroup[] = [];
  let rows = 0;
  for (const group of groups) {
    if (rows >= MAX_ROWS) {
      break;
    }
    const items = group.items.slice(0, Math.min(perGroup, MAX_ROWS - rows));
    if (items.length === 0) {
      continue;
    }
    out.push({ ...group, items });
    rows += items.length;
  }
  return out;
}

/**
 * The palette's row model.
 *
 * With a query it is the search results grouped by kind; with an empty query it
 * is the reader's recent visits plus a static jump list. An empty query is a
 * starting point, not an empty state, so `Recent` is simply absent when there
 * is nothing to recall rather than filled with placeholders.
 */
export function useSearchPaletteGroups(
  term: string,
  results: PaletteSearchResult[],
  visits: Visit[],
): PaletteGroup[] {
  return useMemo(() => {
    if (!term) {
      const recent: PaletteGroup[] = visits.length
        ? [
            {
              id: 'recent',
              label: 'Recent',
              items: visits.map(visitToPaletteItem),
            },
          ]
        : [];
      return capped([
        ...recent,
        { id: 'jump-to', label: 'Jump to', items: JUMP_TO },
      ]);
    }

    const byLabel = new Map<string, PaletteGroup & { order: number }>();
    for (const result of results) {
      const spec = groupSpecFor(result);
      const group = byLabel.get(spec.label) ?? {
        id: spec.label,
        label: spec.label,
        order: spec.order,
        items: [],
      };
      group.items.push(toPaletteItem(result, spec.label));
      byLabel.set(spec.label, group);
    }

    return capped([...byLabel.values()].sort((a, b) => a.order - b.order));
  }, [term, results, visits]);
}
